import React, { useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const money = (value) => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
}).format(Number(value || 0));

const TYPE_LABELS = {
  SIZE: 'Tamanho',
  CRUST: 'Massa',
  EDGE: 'Borda',
  TOPPING: 'Sabores',
  OFFER_UNIT: 'Complementos',
  SPECIFICATION: 'Escolha uma opção',
  INGREDIENTS: 'Ingredientes',
  CUTLERY: 'Talheres',
};

const SelectionRules = ({ group, selectedCount }) => {
  const min = Number(group.qtd_minima || 0);
  const max = Number(group.qtd_maxima || 0);
  const invalid = selectedCount < min || (max > 0 && selectedCount > max);
  let text = 'Opcional';
  if (min > 0 && max > 0 && min === max) text = `Escolha ${min}`;
  else if (min > 0 && max > 0) text = `Escolha de ${min} a ${max}`;
  else if (min > 0) text = `Escolha pelo menos ${min}`;
  else if (max > 0) text = `Escolha até ${max}`;
  return <Chip label={text} size="small" color={invalid ? 'error' : 'default'} variant={invalid ? 'filled' : 'outlined'} />;
};

const ItemPopup = ({
  item,
  onClose,
  onAddToCart,
  complementos = [],
  optionGroups = [],
  aceitaPedido,
}) => {
  const [quantidade, setQuantidade] = React.useState(1);
  const [comps, setComps] = React.useState([]);
  const [selectedOptionIds, setSelectedOptionIds] = React.useState([]);
  const [observacao, setObservacao] = React.useState('');

  React.useEffect(() => {
    setQuantidade(1);
    setObservacao('');
    setComps(complementos.map((comp) => ({ ...comp, selecionado: false })));
    setSelectedOptionIds([]);
  }, [item, complementos, optionGroups]);

  const selectedSet = useMemo(() => new Set(selectedOptionIds.map(Number)), [selectedOptionIds]);
  const selectedSizeId = useMemo(() => {
    const sizeGroup = optionGroups.find((group) => group.tipo === 'SIZE');
    return sizeGroup?.opcoes?.find((option) => selectedSet.has(Number(option.id)))?.id || null;
  }, [optionGroups, selectedSet]);

  const visibleOptions = (group) => (group.opcoes || []).filter((option) => (
    !option.opcao_contexto_id
    || (selectedSizeId && Number(option.opcao_contexto_id) === Number(selectedSizeId))
  ));

  const selectedOptions = useMemo(
    () => optionGroups.flatMap((group) => (
      (group.opcoes || [])
        .filter((option) => selectedSet.has(Number(option.id)))
        .map((option) => ({ ...option, grupo_tipo: group.tipo, grupo_nome: group.titulo }))
    )),
    [optionGroups, selectedSet]
  );

  const groupErrors = useMemo(() => {
    const errors = [];
    optionGroups.forEach((group) => {
      const count = (group.opcoes || []).filter((option) => selectedSet.has(Number(option.id))).length;
      const min = Number(group.qtd_minima || 0);
      const max = Number(group.qtd_maxima || 0);
      if (count < min) errors.push(`Faça as escolhas obrigatórias em ${group.titulo || TYPE_LABELS[group.tipo] || 'Opções'}.`);
      if (max > 0 && count > max) errors.push(`Escolha no máximo ${max} opção(ões) em ${group.titulo || 'Opções'}.`);
    });
    const size = optionGroups.find((group) => group.tipo === 'SIZE')?.opcoes
      ?.find((option) => selectedSet.has(Number(option.id)));
    const flavors = selectedOptions.filter((option) => option.grupo_tipo === 'TOPPING');
    const allowed = Array.isArray(size?.fractions) ? size.fractions.map(Number) : [];
    if (flavors.length && !size) errors.push('Escolha o tamanho antes de escolher os sabores.');
    if (size && flavors.length && allowed.length && !allowed.includes(flavors.length)) {
      errors.push(`Este tamanho aceita ${allowed.join(', ')} sabor(es).`);
    }
    return errors;
  }, [optionGroups, selectedOptions, selectedSet]);

  const legacyRequiredMissing = !optionGroups.length && comps.some((comp) => (
    (comp.obrigatorio || Number(comp.min_qtd || 0) > 0) && !comp.selecionado
  ));
  const legacyTotal = optionGroups.length ? 0 : comps.filter((comp) => comp.selecionado)
    .reduce((total, comp) => total + Number(comp.preco || 0), 0);
  const regularOptionTotal = selectedOptions
    .filter((option) => option.grupo_tipo !== 'TOPPING')
    .reduce((total, option) => total + Number(option.preco || 0), 0);
  const toppingPrice = selectedOptions
    .filter((option) => option.grupo_tipo === 'TOPPING')
    .reduce((highest, option) => Math.max(highest, Number(option.preco || 0)), 0);
  const total = (Number(item?.preco || 0) + legacyTotal + regularOptionTotal + toppingPrice) * quantidade;
  const cannotAdd = legacyRequiredMissing || groupErrors.length > 0;

  const toggleLegacy = (comp, checked) => {
    setComps((current) => current.map((entry) => (
      entry.id === comp.id ? { ...entry, selecionado: checked } : entry
    )));
  };

  const toggleOption = (group, option) => {
    const optionId = Number(option.id);
    setSelectedOptionIds((current) => {
      const next = new Set(current.map(Number));
      if (next.has(optionId)) {
        next.delete(optionId);
      } else {
        if (Number(group.qtd_maxima) === 1) {
          (group.opcoes || []).forEach((entry) => next.delete(Number(entry.id)));
        }
        if (group.tipo === 'SIZE') {
          optionGroups.flatMap((entry) => entry.opcoes || []).forEach((entry) => {
            if (entry.opcao_contexto_id && Number(entry.opcao_contexto_id) !== optionId) {
              next.delete(Number(entry.id));
            }
          });
        }
        const selectedInGroup = (group.opcoes || []).filter((entry) => next.has(Number(entry.id))).length;
        if (Number(group.qtd_maxima) <= 0 || selectedInGroup < Number(group.qtd_maxima)) next.add(optionId);
      }
      return [...next];
    });
  };

  if (!item) return null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '24px' } }}>
      <IconButton onClick={onClose} aria-label="Fechar" sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '240px 1fr' }, gap: 3, alignItems: 'start' }}>
          <Box
            sx={{
              width: '100%', height: { xs: 220, sm: 300 }, borderRadius: 3, bgcolor: 'grey.200',
              backgroundImage: item.foto_url ? `url(${item.foto_url})` : 'none',
              backgroundSize: 'cover', backgroundPosition: 'center',
            }}
          />
          <Stack spacing={2}>
            <Box>
              <Typography variant="h4" fontWeight={800} pr={4}>{item.nome || ''}</Typography>
              <Typography variant="h5" fontWeight={800} mt={0.5}>{money(item.preco)}</Typography>
              {item.descricao && <Typography color="text.secondary" mt={1}>{item.descricao}</Typography>}
            </Box>

            {optionGroups.map((group) => {
              const options = visibleOptions(group);
              const selectedCount = (group.opcoes || []).filter((option) => selectedSet.has(Number(option.id))).length;
              return (
                <Box key={group.id}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} mb={0.5}>
                    <Box>
                      <Typography fontWeight={800}>{group.titulo || TYPE_LABELS[group.tipo] || 'Opções'}</Typography>
                      {group.tipo !== 'OFFER_UNIT' && group.titulo && (
                        <Typography variant="caption" color="text.secondary">{TYPE_LABELS[group.tipo]}</Typography>
                      )}
                    </Box>
                    <SelectionRules group={group} selectedCount={selectedCount} />
                  </Stack>
                  <Stack divider={<Divider flexItem />}>
                    {options.map((option) => {
                      const selected = selectedSet.has(Number(option.id));
                      const Control = Number(group.qtd_maxima) === 1 ? Radio : Checkbox;
                      return (
                        <Stack key={option.id} direction="row" alignItems="center" spacing={1} py={0.5}>
                          {option.foto_url && <Avatar src={option.foto_url} variant="rounded" />}
                          <FormControlLabel
                            sx={{ flex: 1, m: 0 }}
                            control={<Control checked={selected} onClick={() => toggleOption(group, option)} />}
                            label={option.nome}
                          />
                          {Number(option.preco || 0) > 0 && (
                            <Typography variant="body2" fontWeight={700}>+ {money(option.preco)}</Typography>
                          )}
                        </Stack>
                      );
                    })}
                    {!options.length && group.tipo === 'TOPPING' && !selectedSizeId && (
                      <Typography variant="body2" color="text.secondary" py={1}>Escolha o tamanho para ver os sabores.</Typography>
                    )}
                  </Stack>
                </Box>
              );
            })}

            {!optionGroups.length && comps.length > 0 && (
              <Box>
                <Typography fontWeight={800}>Complementos</Typography>
                {comps.map((comp) => (
                  <FormControlLabel
                    key={comp.id}
                    control={<Checkbox checked={!!comp.selecionado} onChange={(event) => toggleLegacy(comp, event.target.checked)} />}
                    label={`${comp.nome} · ${money(comp.preco || 0)}`}
                  />
                ))}
              </Box>
            )}

            {aceitaPedido && (
              <>
                {cannotAdd && (
                  <Typography variant="body2" color="error.main">
                    {groupErrors[0] || 'Selecione os complementos obrigatórios.'}
                  </Typography>
                )}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <TextField
                    label="Quantidade"
                    type="number"
                    value={quantidade}
                    onChange={(event) => setQuantidade(Math.max(1, parseInt(event.target.value, 10) || 1))}
                    inputProps={{ min: 1 }}
                    sx={{ width: 120 }}
                  />
                  <Typography fontWeight={800}>Total: {money(total)}</Typography>
                </Stack>
                <TextField
                  label="Observação"
                  value={observacao}
                  onChange={(event) => setObservacao(event.target.value)}
                  fullWidth multiline minRows={2}
                />
                <Button
                  variant="contained"
                  disabled={cannotAdd}
                  onClick={() => {
                    onAddToCart?.({
                      ...item,
                      quantidade,
                      observacao,
                      complementosSelecionados: comps.filter((comp) => comp.selecionado),
                      opcoesSelecionadas: selectedOptions,
                    });
                    onClose();
                  }}
                >
                  Adicionar ao carrinho
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ItemPopup;
